import classNames from 'classnames/bind';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useState } from 'react';
import { Dropdown as SemanticUiDropdown } from 'semantic-ui-react';
import Button, { ButtonMode } from '../Button';
import Icon, { ICONS } from '../Icon';
import styles from './Dropdown.module.scss';
import { DropdownProps } from './Dropdown.types';

const cx = classNames.bind(styles);

const caretIcon = (
  <div className={cx('caret-icon')}>
    <Icon name={ICONS.CaretDown} height={12} width={12} />
  </div>
);

const Dropdown = ({
  className,
  clearable = false,
  dataTestId,
  icon,
  onClose,
  onOpen,
  options,
  onOptionSelect,
  text,
  value,
  withCaretIcon,
  withCheckmarks,
  isNested,
  trigger,
  isActive,
  isLeftAligned = false,
  ...rest
}: DropdownProps): React.ReactElement => {
  const [isClearable, setIsClearable] = useState(false);
  const [searchValue, setSearchValue] = useState(null);
  const [isClearingSearch, setIsClearingSearch] = useState(false);

  // In order to use clear button for 2 different actions (clear search query or clear selected value),
  // clear search lifecycle events have to be registered since onClose is happening before onClearClick
  const startClearingSearch = () => setIsClearingSearch(true);
  const stopClearingSearch = () => setIsClearingSearch(false);
  const delayStopClearingSearch = useCallback(
    debounce(() => {
      stopClearingSearch();
    }, 500),
    []
  );

  const onDropdownClose = (e, data) => {
    if (searchValue) {
      startClearingSearch();
      setSearchValue(null);
      delayStopClearingSearch();
    }

    onClose?.(e, data);
  };

  const onClearClick = () => {
    if (!isClearingSearch) {
      onOptionSelect('');
    } else {
      stopClearingSearch();
    }
  };

  const onSelectedValueChange = (_, data) => {
    onOptionSelect(data.value);
  };

  const onSearchChange = (_, data) => {
    setSearchValue(data.searchQuery);
  };

  const optionsWithCheckmarks = options?.map((option) => ({
    ...option,
    content: (
      <div className={cx('option')}>
        {option.text}
        {option.value === (value || rest.defaultValue) && (
          <span className={cx('checkmark')}>
            <Icon name={ICONS.Checkmark} height={10} width={10} ariaLabel="" tabIndex={-1} />
          </span>
        )}
      </div>
    ),
  }));

  // By default Semantic Dropdown shows focused item label even if it's not yet selected
  // this logic makes it show placeholder text until an item is selected unless other label text is passed which would override this logic
  const dropdownText = text || options?.find((option) => option.value === value)?.text?.toString() || rest?.placeholder;

  useEffect(() => {
    setIsClearable(searchValue || (clearable && !!value));
  }, [searchValue, value]);

  return (
    <div className={cx('body')}>
      <SemanticUiDropdown
        className={cx('dropdown', className, {
          'is-nested': isNested,
          'left-aligned': isLeftAligned,
          'custom-trigger': !!trigger,
          active: isActive,
        })}
        data-testid={dataTestId}
        icon={withCaretIcon ? caretIcon : icon}
        noResultsMessage={`No matching results for "${searchValue}"`}
        onOpen={onOpen}
        onSearchChange={onSearchChange}
        options={withCheckmarks ? optionsWithCheckmarks : options}
        selectOnBlur={false}
        text={dropdownText}
        value={value}
        {...rest}
        onChange={onSelectedValueChange}
        onClose={onDropdownClose}
        selectOnNavigation={false}
        trigger={trigger}
      />
      {isClearable && (
        <Button
          className={cx('clear-button')}
          mode={ButtonMode.TERTIARY}
          onClick={onClearClick}
          ariaLabel="Clear"
          data-testid="clear-button"
        >
          <Icon name={ICONS.Times} />
        </Button>
      )}
    </div>
  );
};

export default Dropdown;
